import {
  Button,
  FormField,
  InputPassword,
  ModalBody,
  ModalFooter,
} from "@aurem/ui";
import { useMemo } from "react";
import { useTranslations } from "../i18n";
import { usePasswordVerification } from "../use-password-verification";

type Props = {
  onClose: () => void;
  generateMfa?: (password: string) => Promise<ArrayBuffer>;
  onSuccess?: (result: string) => void;
  mode?: "mfa-enable" | "reauthentication";
  errorMessage?: string | null | undefined;
};

export const PasswordConfirmationForm = ({
  onClose,
  generateMfa,
  onSuccess,
  mode = "mfa-enable",
  errorMessage,
}: Props) => {
  const { t } = useTranslations();

  const labels = useMemo(() => {
    if (mode === "reauthentication") {
      return {
        passwordLabel: t(
          "apps.employee.reauthentication.password.passwordLabel",
        ),
        passwordPlaceholder: t(
          "apps.employee.reauthentication.password.passwordPlaceholder",
        ),
        errorMessage: t("apps.employee.reauthentication.password.error"),
        cancel: t("apps.employee.common.cancel"),
        confirm: t("apps.employee.common.confirm"),
      };
    }
    return {
      passwordLabel: t(
        "apps.admin.pages.profile.page.mfa.passwordRequest.password",
      ),
      passwordPlaceholder: t(
        "apps.admin.pages.profile.page.mfa.passwordRequest.enterPassword",
      ),
      errorMessage: t(
        "apps.admin.pages.profile.page.mfa.passwordRequest.error",
      ),
      cancel: t("apps.admin.common.cancel"),
      confirm: t("apps.admin.common.confirm"),
    };
  }, [mode, t]);

  const { password, loading, error, handleChange, handleSubmit } =
    usePasswordVerification(
      mode,
      generateMfa,
      onSuccess,
      labels.errorMessage,
      errorMessage,
    );

  return (
    <>
      <ModalBody>
        <form
          id="password-confirmation-form"
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          onReset={onClose}
        >
          <FormField message={error}>
            <InputPassword
              value={password}
              onChange={handleChange}
              data-e2e-id="profile__input__mfaEnablePassword"
              placeholder={labels.passwordPlaceholder}
              name="password"
              autoFocus
            />
          </FormField>
        </form>
      </ModalBody>
      <ModalFooter className="border-none pt-0">
        <Button
          form="password-confirmation-form"
          variant="outlined"
          color="neutral"
          data-e2e-id="profile__button__mfaEnableCancel"
          type="reset"
        >
          {labels.cancel}
        </Button>
        <Button
          form="password-confirmation-form"
          data-e2e-id="profile__button__mfaEnableSubmit"
          disabled={loading}
          type="submit"
        >
          {labels.confirm}
        </Button>
      </ModalFooter>
    </>
  );
};
